import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import SidebarNav from "../sidebar";
import {
  listComplianceConfig,
  upsertComplianceConfig,
  deleteComplianceConfig,
  getRiskIndicatorCatalog,
  getCoin,
} from "../../../service/api";
import RiskPolicyEditor from "./RiskPolicyEditor";
import PermissionGate from "../commoncomponent/PermissionGate";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";
import CustomPagination from "../Pagination/pagination";

// `ALL_COLUMNS` defines the visible-by-default vs hidden-by-default split.
// The actual render functions (which use coins/updateRow/saveOverride/removeOverride)
// have to live inside the component, so we just describe the column metadata here
// and zip in the real renders below.
const ALL_COLUMNS_META = [
  { key: "coin", title: "Coin", defaultVisible: true },
  { key: "depositHoldThreshold", title: "Deposit hold ≥", defaultVisible: true },
  { key: "withdrawPrescreenThreshold", title: "Withdraw pre-screen ≥", defaultVisible: true },
  { key: "depositAutoRejectThreshold", title: "Auto-reject ≥", defaultVisible: true },
  { key: "indicatorBlacklist", title: "Blacklist indicators", defaultVisible: false },
  { key: "holdNotifyTelegram", title: "Telegram", defaultVisible: false },
];

const EMPTY_GLOBAL = {
  scope: "GLOBAL",
  coinId: null,
  depositHoldThreshold: 60,
  withdrawPrescreenThreshold: 60,
  depositAutoRejectThreshold: null,
  indicatorBlacklist: ["sanctions", "ofac"],
  // Weighted AML indicator framework
  riskIndicatorPolicies: [],
  fatfBlacklist: [],
  fatfGreylist: [],
  highRiskJurisdictions: [],
  highRiskOccupations: [],
  behavioral: {},
  holdNotifyTelegram: true,
  // Single global on/off switch for the screening providers (applies to every coin).
  trmEnabled: false,
  notabeneEnabled: false,
};

const BEHAVIORAL_FIELDS = [
  {
    key: "velocityWindowHours",
    label: "Time window (hours)",
    def: 24,
    tip: "Rolling time window in hours for counting transactions and total volume. Transactions older than this window are not counted.",
  },
  {
    key: "velocityMaxTxCount",
    label: "Max tx count",
    def: 20,
    tip: "Maximum number of deposits or withdrawals allowed within the velocity window before a velocity_anomaly flag fires.",
  },
  {
    key: "velocityMaxVolumeUsd",
    label: "Max volume (USD)",
    def: 50000,
    tip: "Maximum total USD volume allowed within the velocity window. Exceeded even at low tx count → velocity_anomaly fires.",
  },
  {
    key: "structuringWindowHours",
    label: "Time window (hours)",
    def: 24,
    tip: "Rolling time window in hours used to detect structuring patterns across multiple small deposits.",
  },
  {
    key: "structuringMinCount",
    label: "Min deposits",
    def: 5,
    tip: "Minimum number of small deposits within the window to qualify as a structuring pattern. A single large deposit is not structuring.",
  },
  {
    key: "structuringAggregateUsd",
    label: "Aggregate threshold (USD)",
    def: 10000,
    tip: "If the deposits within the window reach this total USD amount, the pattern is flagged as structuring. Set near your local reporting threshold.",
  },
  {
    key: "rapidInOutWindowMinutes",
    label: "Window (minutes)",
    def: 60,
    tip: "If a user requests a withdrawal within this many minutes of a deposit, it is flagged as rapid in/out — a common layering technique.",
  },
];

const BEHAVIORAL_FIELDS_MAP = Object.fromEntries(BEHAVIORAL_FIELDS.map((f) => [f.key, f]));

const BEHAVIORAL_GROUPS = [
  {
    key: "velocity",
    title: "Velocity Detection",
    color: "#1677ff",
    description: "Flags users making too many transactions or moving too much volume in a short time.",
    testExample: "Set Max tx = 3, Time window = 1 hr → make 4 deposits within 1 hr → 4th deposit flags velocity_anomaly.",
    fields: ["velocityWindowHours", "velocityMaxTxCount", "velocityMaxVolumeUsd"],
  },
  {
    key: "structuring",
    title: "Structuring Detection",
    color: "#fa8c16",
    description: "Detects many small deposits that add up to a large amount (common money laundering technique).",
    testExample: "Set Min deposits = 3, Aggregate = $500 → make 3 deposits totalling $501 in window → flags structuring.",
    fields: ["structuringWindowHours", "structuringMinCount", "structuringAggregateUsd"],
  },
  {
    key: "rapidInOut",
    title: "Rapid In / Out",
    color: "#52c41a",
    description: "Flags users who request a withdrawal shortly after depositing — a layering indicator.",
    testExample: "Set window = 1440 (24 hrs) → make a deposit, then request a withdrawal the same day → flags rapid_in_out.",
    fields: ["rapidInOutWindowMinutes"],
  },
];

const ComplianceConfigPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [coins, setCoins] = useState([]);
  const [catalog, setCatalog] = useState([]);

  const [globalForm, setGlobalForm] = useState({ ...EMPTY_GLOBAL });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    coinId: "",
    depositHoldThreshold: 60,
    withdrawPrescreenThreshold: 60,
    depositAutoRejectThreshold: null,
    indicatorBlacklist: [],
    holdNotifyTelegram: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await listComplianceConfig();
      if (res?.status) {
        const list = res.data || [];
        const glob = list.find((r) => r.scope === "GLOBAL");
        setGlobalForm(glob ? { ...EMPTY_GLOBAL, ...glob } : { ...EMPTY_GLOBAL });
        setRows(list.filter((r) => r.scope === "COIN"));
      } else {
        toast.error(res?.message || "Load failed", { toastId: 'compliance-config-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", { toastId: 'compliance-config-error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getRiskIndicatorCatalog()
      .then((res) => { if (res?.status) setCatalog(res.data || []); })
      .catch(() => {});
    getCoin({ page: 1, sizePerPage: 100, pagination: false }).then((res) => {
      if (res?.status) {
        const list = Array.isArray(res?.data?.result)
          ? res.data.result
          : res?.data || [];
        setCoins(list);
      }
    });
  }, []);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      const res = await upsertComplianceConfig({
        scope: "GLOBAL",
        coinId: null,
        depositHoldThreshold: Number(globalForm.depositHoldThreshold),
        withdrawPrescreenThreshold: Number(
          globalForm.withdrawPrescreenThreshold,
        ),
        depositAutoRejectThreshold:
          globalForm.depositAutoRejectThreshold == null ||
          globalForm.depositAutoRejectThreshold === ""
            ? null
            : Number(globalForm.depositAutoRejectThreshold),
        indicatorBlacklist: globalForm.indicatorBlacklist || [],
        riskIndicatorPolicies: globalForm.riskIndicatorPolicies || [],
        fatfBlacklist: globalForm.fatfBlacklist || [],
        fatfGreylist: globalForm.fatfGreylist || [],
        highRiskJurisdictions: globalForm.highRiskJurisdictions || [],
        highRiskOccupations: globalForm.highRiskOccupations || [],
        behavioral: globalForm.behavioral || {},
        holdNotifyTelegram: !!globalForm.holdNotifyTelegram,
        trmEnabled: !!globalForm.trmEnabled,
        notabeneEnabled: !!globalForm.notabeneEnabled,
      });
      if (res?.status) {
        toast.success("Global compliance config saved");
        load();
      } else {
        toast.error(res?.message || "Save failed", { toastId: 'compliance-config-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: 'compliance-config-error' });
    } finally {
      setSaving(false);
    }
  };

  const saveOverride = async (row) => {
    try {
      const res = await upsertComplianceConfig({
        scope: "COIN",
        coinId: row.coinId,
        depositHoldThreshold: Number(row.depositHoldThreshold),
        withdrawPrescreenThreshold: Number(row.withdrawPrescreenThreshold),
        depositAutoRejectThreshold:
          row.depositAutoRejectThreshold == null ||
          row.depositAutoRejectThreshold === ""
            ? null
            : Number(row.depositAutoRejectThreshold),
        indicatorBlacklist: row.indicatorBlacklist || [],
        holdNotifyTelegram: !!row.holdNotifyTelegram,
      });
      if (res?.status) {
        toast.success("Override saved");
        load();
      } else {
        toast.error(res?.message || "Save failed", { toastId: 'compliance-config-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: 'compliance-config-error' });
    }
  };

  const removeOverride = async (id) => {
    if (deletingId) return;
    try {
      setDeletingId(id);
      const res = await deleteComplianceConfig(id);
      if (res?.status) {
        toast.success("Override removed");
        load();
      } else {
        toast.error(res?.message || "Delete failed", { toastId: 'compliance-config-error' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", { toastId: 'compliance-config-error' });
    } finally {
      setDeletingId(null);
    }
  };

  const submitAdd = async () => {
    if (!addForm.coinId) {
      toast.error("Pick a coin", { toastId: 'compliance-config-error' });
      return;
    }
    await saveOverride(addForm);
    setAddOpen(false);
    setAddForm({
      coinId: "",
      depositHoldThreshold: 60,
      withdrawPrescreenThreshold: 60,
      depositAutoRejectThreshold: null,
      indicatorBlacklist: [],
      holdNotifyTelegram: true,
    });
  };

  const coinOptions = useMemo(
    () =>
      coins.map((c) => ({
        label: `${c.symbol || c.name} — ${c.name}`,
        value: c._id,
      })),
    [coins],
  );

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const columnRenderers = {
    coin: (r) => {
      const c = coins.find((x) => x._id === r.coinId);
      return c ? (
        <b>{c.symbol || c.name}</b>
      ) : (
        <span style={{ opacity: 0.5 }}>{String(r.coinId).slice(-6)}</span>
      );
    },
    depositHoldThreshold: (r) => (
      <InputNumber
        size="small"
        value={r.depositHoldThreshold}
        min={0}
        max={100}
        onChange={(v) => updateRow(r._id, { depositHoldThreshold: v ?? 0 })}
      />
    ),
    withdrawPrescreenThreshold: (r) => (
      <InputNumber
        size="small"
        value={r.withdrawPrescreenThreshold}
        min={0}
        max={100}
        onChange={(v) =>
          updateRow(r._id, { withdrawPrescreenThreshold: v ?? 0 })
        }
      />
    ),
    depositAutoRejectThreshold: (r) => (
      <InputNumber
        size="small"
        value={r.depositAutoRejectThreshold ?? undefined}
        placeholder="off"
        min={0}
        max={100}
        onChange={(v) =>
          updateRow(r._id, { depositAutoRejectThreshold: v ?? null })
        }
      />
    ),
    indicatorBlacklist: (r) => (
      <Select
        mode="tags"
        size="small"
        style={{ minWidth: 220 }}
        value={r.indicatorBlacklist || []}
        onChange={(v) =>
          updateRow(r._id, {
            indicatorBlacklist: v.map((s) => String(s).toLowerCase()),
          })
        }
      />
    ),
    holdNotifyTelegram: (r) => (
      <Switch
        size="small"
        checked={!!r.holdNotifyTelegram}
        onChange={(v) => updateRow(r._id, { holdNotifyTelegram: v })}
      />
    ),
  };

  const ALL_COLUMNS = useMemo(
    () =>
      ALL_COLUMNS_META.map((c) => ({
        ...c,
        render: columnRenderers[c.key] || ((r) => "-"),
      })),
    [coins],
  );

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "compliance-config-list",
    allColumns: ALL_COLUMNS,
  });

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((r) => {
      const c = coins.find((x) => x._id === r.coinId);
      const symbol = (c?.symbol || "").toLowerCase();
      const name = (c?.name || "").toLowerCase();
      return symbol.includes(q) || name.includes(q);
    });
  }, [rows, coins, searchTerm]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm, rows.length]);

  const pagedRows = useMemo(
    () =>
      filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const handleDownload = (format) => {
    if (!filteredRows?.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
    const exportCols = [
      { key: "coin", title: "Coin", render: (r) => coins?.find((c) => c?._id === r?.coinId)?.symbol ?? r?.coinId ?? "-" },
      { key: "depositHoldThreshold", title: "Deposit hold ≥", render: (r) => r?.depositHoldThreshold ?? "-" },
      { key: "withdrawPrescreenThreshold", title: "Withdraw pre-screen ≥", render: (r) => r?.withdrawPrescreenThreshold ?? "-" },
      { key: "depositAutoRejectThreshold", title: "Auto-reject ≥", render: (r) => r?.depositAutoRejectThreshold ?? "-" },
      { key: "indicatorBlacklist", title: "Blacklist indicators", render: (r) => (r?.indicatorBlacklist ?? [])?.join(", ") },
      { key: "holdNotifyTelegram", title: "Telegram notify", render: (r) => r?.holdNotifyTelegram ? "Yes" : "No" },
    ];
    triggerDownload(format, filteredRows, exportCols, "compliance-config");
  };

  const actionColumn = {
    key: "__action",
    title: "",
    width: 160,
    render: (_, r) => (
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <Button size="small" type="primary" onClick={() => saveOverride(r)}>
          Save
        </Button>
        <Popconfirm
          title="Remove this override?"
          description="This action cannot be undone."
          onConfirm={() => removeOverride(r._id)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: deletingId === r._id, disabled: deletingId === r._id }}
          cancelButtonProps={{ disabled: deletingId === r._id }}
          disabled={!!deletingId && deletingId !== r._id}
        >
          <Button size="small" danger loading={deletingId === r._id} disabled={!!deletingId && deletingId !== r._id}>
            Remove
          </Button>
        </Popconfirm>
      </div>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid" style={{ padding: 24 }}>
          <h3>Compliance Configuration</h3>
          <p style={{ opacity: 0.7, marginBottom: 16 }}>
            Risk thresholds used by the deposit + withdraw screening pipeline.
            The GLOBAL row applies to every coin. Per-coin overrides replace
            the GLOBAL thresholds for that coin only.
          </p>

          {loading && <Spin />}

          {/* ---- GLOBAL ---- */}
          <div
            className="card"
            style={{ marginBottom: 24, padding: 16, background: "white" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h5 style={{ margin: 0 }}>
                GLOBAL <Tag color="blue">default</Tag>
              </h5>
              <PermissionGate module="compliance-config" action="update">
                <Button
                  type="primary"
                  loading={saving}
                  onClick={saveGlobal}
                >
                  Save GLOBAL
                </Button>
              </PermissionGate>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 16,
              }}
            >
              <div>
                <label style={{ fontSize: 12, opacity: 0.7 }}>
                  Deposit hold threshold (0–100){" "}
                  <Tooltip title="Composite risk score (0–100) at or above this value puts the deposit ON_HOLD for manual admin review. TRM 'High' risk maps to roughly 60–70. Set lower to catch more; set higher to reduce false positives.">
                    <QuestionCircleOutlined style={{ fontSize: 11 }} />
                  </Tooltip>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  value={globalForm.depositHoldThreshold}
                  min={0}
                  max={100}
                  onChange={(v) =>
                    setGlobalForm({
                      ...globalForm,
                      depositHoldThreshold: v ?? 0,
                    })
                  }
                />
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                  Composite risk score at or above this value → deposit goes
                  to ON_HOLD.
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.7 }}>
                  Withdraw pre-screen threshold (0–100){" "}
                  <Tooltip title="TRM screens the destination wallet before funds leave. If the risk score meets or exceeds this value, the withdrawal is paused for admin review. Recommended: match the deposit hold threshold.">
                    <QuestionCircleOutlined style={{ fontSize: 11 }} />
                  </Tooltip>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  value={globalForm.withdrawPrescreenThreshold}
                  min={0}
                  max={100}
                  onChange={(v) =>
                    setGlobalForm({
                      ...globalForm,
                      withdrawPrescreenThreshold: v ?? 0,
                    })
                  }
                />
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                  TRM-screen the destination before sending; at or above this
                  value → withdrawal goes to ON_HOLD.
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.7 }}>
                  Auto-reject threshold (optional){" "}
                  <Tooltip title="Optional hard cutoff. When set, any deposit or withdrawal scoring at or above this value is automatically REJECTED — funds are never credited or sent. Leave blank to always hold high-risk transactions for manual review instead of auto-rejecting. Use with caution: set well above the hold threshold (e.g. 90+) to avoid false rejections.">
                    <QuestionCircleOutlined style={{ fontSize: 11 }} />
                  </Tooltip>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="leave blank to disable"
                  value={globalForm.depositAutoRejectThreshold ?? undefined}
                  min={0}
                  max={100}
                  onChange={(v) =>
                    setGlobalForm({
                      ...globalForm,
                      depositAutoRejectThreshold: v ?? null,
                    })
                  }
                />
                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
                  Scores at or above this value are REJECTED automatically
                  instead of put on hold.
                </div>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 12, opacity: 0.7 }}>
                  Indicator blacklist (any of these in TRM result → always HOLD)
                </label>
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  value={globalForm.indicatorBlacklist || []}
                  onChange={(v) =>
                    setGlobalForm({
                      ...globalForm,
                      indicatorBlacklist: v.map((s) =>
                        String(s).toLowerCase(),
                      ),
                    })
                  }
                  placeholder="sanctions, mixer, ofac, …"
                />
              </div>
              {/* Notify Telegram on hold — hidden (feature disabled)
              <div>
                <label style={{ fontSize: 12, opacity: 0.7 }}>
                  Notify Telegram on hold
                </label>
                <div>
                  <Switch
                    checked={!!globalForm.holdNotifyTelegram}
                    onChange={(v) =>
                      setGlobalForm({ ...globalForm, holdNotifyTelegram: v })
                    }
                  />
                </div>
              </div>
              */}
              <div style={{ gridColumn: "span 3", display: "flex", flexWrap: "wrap", gap: 48 }}>
                <div>
                  <label style={{ fontSize: 12, opacity: 0.7 }}>
                    TRM screening (AML) — global
                  </label>
                  <div>
                    <Switch
                      checked={!!globalForm.trmEnabled}
                      onChange={(v) =>
                        setGlobalForm({ ...globalForm, trmEnabled: v })
                      }
                    />
                    <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 8 }}>
                      Screen deposits & withdrawals with TRM Labs.
                    </span>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, opacity: 0.7 }}>
                    Notabene Travel Rule — global
                  </label>
                  <div>
                    <Switch
                      checked={!!globalForm.notabeneEnabled}
                      onChange={(v) =>
                        setGlobalForm({ ...globalForm, notabeneEnabled: v })
                      }
                    />
                    <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 8 }}>
                      Run the Notabene Travel Rule flow on withdrawals.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Weighted risk-indicator framework ── */}
            <div style={{ marginTop: 20 }}>
              <RiskPolicyEditor
                catalog={catalog}
                value={globalForm.riskIndicatorPolicies || []}
                onChange={(p) => setGlobalForm({ ...globalForm, riskIndicatorPolicies: p })}
              />
            </div>

            {/* ── Geographic + customer lists ── */}
            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[
                {
                  label: "FATF black-list (ISO country codes)",
                  field: "fatfBlacklist",
                  ph: "KP, IR, MM, …",
                  tip: "Countries on the FATF 'High-Risk Jurisdictions subject to a Call for Action' list. Deposits or withdrawals from users whose country or nationality is on this list are flagged under the Geographic Risk dimension with the highest weight. Use ISO 3166-1 alpha-2 codes (e.g. KP = North Korea, IR = Iran, MM = Myanmar).",
                },
                {
                  label: "FATF grey-list (ISO country codes)",
                  field: "fatfGreylist",
                  ph: "VN, ZA, PK, …",
                  tip: "Countries on the FATF 'Jurisdictions under Increased Monitoring' list. These countries have AML/CFT deficiencies but are actively working to fix them. Flagged with a lower weight than the black list. Use ISO 3166-1 alpha-2 codes (e.g. VN = Vietnam, ZA = South Africa, PK = Pakistan).",
                },
                {
                  label: "High-risk jurisdictions (ISO codes)",
                  field: "highRiskJurisdictions",
                  ph: "AF, SY, YE, …",
                  tip: "Your exchange's own elevated-risk country list, independent of FATF status. Use this for countries where your risk appetite is lower — e.g. high fraud rates, weak AML enforcement, or sanctions context. ISO 3166-1 alpha-2 codes.",
                },
                {
                  label: "High-risk occupations",
                  field: "highRiskOccupations",
                  ph: "self employed, crypto trader, …",
                  tip: "User-declared occupations your compliance team flags for enhanced due diligence. Matched against the occupation field collected during KYC. Common examples: 'self employed', 'crypto trader', 'politician', 'money services'. Triggers the Customer Risk dimension.",
                },
              ].map(({ label, field, ph, tip }) => (
                <div key={field}>
                  <label style={{ fontSize: 12, opacity: 0.7 }}>
                    {label}{" "}
                    <Tooltip title={tip}>
                      <QuestionCircleOutlined style={{ fontSize: 11 }} />
                    </Tooltip>
                  </label>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    value={globalForm[field] || []}
                    onChange={(v) => setGlobalForm({ ...globalForm, [field]: v })}
                    placeholder={ph}
                  />
                </div>
              ))}
            </div>

            {/* ── Behavioral thresholds ── */}
            <div style={{ marginTop: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Behavioral risk thresholds{" "}
                <Tooltip title="Controls three behavioral AML patterns the engine detects on every transaction: velocity anomalies (too many/too much too fast), structuring (many small deposits adding up), and rapid in/out (deposit then quick withdrawal). Flags are written to the user's RiskState and feed the Behavioral Risk dimension.">
                  <QuestionCircleOutlined style={{ fontSize: 12 }} />
                </Tooltip>
              </label>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {BEHAVIORAL_GROUPS.map((group) => (
                  <div
                    key={group.key}
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Card header */}
                    <div
                      style={{
                        borderLeft: `3px solid ${group.color}`,
                        padding: "10px 12px",
                        background: "#fafafa",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 12, color: group.color }}>
                        {group.title}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 3 }}>
                        {group.description}
                      </div>
                    </div>

                    {/* Fields */}
                    <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                      {group.fields.map((fkey) => {
                        const f = BEHAVIORAL_FIELDS_MAP[fkey];
                        if (!f) return null;
                        return (
                          <div key={fkey} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <label style={{ fontSize: 11, opacity: 0.65, flex: 1, marginBottom: 0 }}>
                              {f.label}{" "}
                              <Tooltip title={f.tip}>
                                <QuestionCircleOutlined style={{ fontSize: 9, opacity: 0.7 }} />
                              </Tooltip>
                            </label>
                            <InputNumber
                              size="small"
                              style={{ width: 100, flexShrink: 0 }}
                              min={0}
                              value={globalForm.behavioral?.[f.key] ?? f.def}
                              onChange={(v) =>
                                setGlobalForm({
                                  ...globalForm,
                                  behavioral: { ...(globalForm.behavioral || {}), [f.key]: v ?? f.def },
                                })
                              }
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Test example footer */}
                    <div
                      style={{
                        padding: "7px 12px",
                        background: "#f0f7ff",
                        borderTop: "1px solid #d6e8ff",
                      }}
                    >
                      <span style={{ fontSize: 10, color: "#1677ff", fontWeight: 600 }}>
                        How to test:{" "}
                      </span>
                      <span style={{ fontSize: 10, color: "#1677ff" }}>
                        {group.testExample}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---- PER-COIN OVERRIDES ---- */}
          <div className="list-layout">
            <div className="list-layout__main">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <h5 style={{ margin: 0 }}>Per-coin overrides</h5>
                <PermissionGate module="compliance-config" action="update">
                  <Button onClick={() => setAddOpen(true)} type="primary">
                    + Add override
                  </Button>
                </PermissionGate>
              </div>
              <div className="card">
                <div className="card-body">
                  {rows.length === 0 ? (
                    <Alert
                      type="info"
                      showIcon
                      message="No per-coin overrides. GLOBAL settings apply to every coin."
                    />
                  ) : (
                    <>
                      <TableToolbar
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                        total={filteredRows.length}
                        page={page}
                        visibleColumnKeys={visibleColumnKeys}
                        allColumns={ALL_COLUMNS}
                        toggleColumn={toggleColumn}
                        resetColumns={resetColumns}
                        onDownload={handleDownload}
                      />
                      <Table
                        rowKey={(r) => r._id}
                        columns={columns}
                        dataSource={pagedRows}
                        size="small"
                        pagination={false}
                      />
                      {filteredRows.length > pageSize && (
                        <CustomPagination
                          key={`pg-${pageSize}`}
                          totalTabs={filteredRows.length}
                          tabsPerPage={pageSize}
                          handleChange={setPage}
                          className="display-flex"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={filteredRows.length}
                onClear={() => setSearchTerm("")}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Coin name or symbol"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                    disabled={rows.length === 0}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Add per-coin override"
        open={addOpen}
        onOk={submitAdd}
        onCancel={() => setAddOpen(false)}
        okText="Save override"
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>Coin</label>
            <Select
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              placeholder="Pick a coin"
              options={coinOptions}
              value={addForm.coinId || undefined}
              onChange={(v) => setAddForm({ ...addForm, coinId: v })}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, opacity: 0.7 }}>
                Deposit hold threshold
              </label>
              <InputNumber
                style={{ width: "100%" }}
                value={addForm.depositHoldThreshold}
                min={0}
                max={100}
                onChange={(v) =>
                  setAddForm({ ...addForm, depositHoldThreshold: v ?? 0 })
                }
              />
            </div>
            <div>
              <label style={{ fontSize: 12, opacity: 0.7 }}>
                Withdraw pre-screen threshold
              </label>
              <InputNumber
                style={{ width: "100%" }}
                value={addForm.withdrawPrescreenThreshold}
                min={0}
                max={100}
                onChange={(v) =>
                  setAddForm({
                    ...addForm,
                    withdrawPrescreenThreshold: v ?? 0,
                  })
                }
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              Auto-reject threshold (optional)
            </label>
            <InputNumber
              style={{ width: "100%" }}
              value={addForm.depositAutoRejectThreshold ?? undefined}
              placeholder="leave blank to disable"
              min={0}
              max={100}
              onChange={(v) =>
                setAddForm({
                  ...addForm,
                  depositAutoRejectThreshold: v ?? null,
                })
              }
            />
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              Indicator blacklist
            </label>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              value={addForm.indicatorBlacklist || []}
              onChange={(v) =>
                setAddForm({
                  ...addForm,
                  indicatorBlacklist: v.map((s) => String(s).toLowerCase()),
                })
              }
            />
          </div>
          {/* Notify Telegram on hold — hidden (feature disabled)
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Switch
              size="small"
              checked={!!addForm.holdNotifyTelegram}
              onChange={(v) =>
                setAddForm({ ...addForm, holdNotifyTelegram: v })
              }
            />
            Notify Telegram on hold
          </label>
          */}
        </div>
      </Modal>
    </>
  );
};

export default ComplianceConfigPage;
